import { httpClient, HttpError } from "@coworker/http-client";

const PRESENTATION_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.presentationml.presentation";
const WORKBOOK_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

type UploadDetail = { attribute: string; value: string };
type UploadArticle = {
  product_article_id: string;
  item_name: string;
  count: number;
};
type UploadPreview = { url: string; relative_path: string };
export type UploadWarning = {
  code: string;
  message: string;
  resolution?: { action: string; label: string };
};
export type MediaLaunch = {
  name: string;
  year: number;
  launch_number?: number | null;
};
export type DuplicateAction = "CREATE_NEW" | "OVERRIDE";
type ExistingMedia = {
  media_id: string;
  name: string;
  source_slide_number: number | null;
  launch_id: string | null;
};
type DuplicatePresentationResponse = {
  code: "DUPLICATE_PRESENTATION";
  existing_media: ExistingMedia[];
  allowed_actions?: DuplicateAction[];
};

export class DuplicatePresentationError extends Error {
  constructor(readonly duplicate: DuplicatePresentationResponse) {
    super("Media from this presentation already exist.");
  }
}

export type UploadMedia = {
  name: string;
  active: string;
  media_type: string;
  details: UploadDetail[];
  articles: UploadArticle[];
  source: {
    slide_number: number;
    image_descriptions: string[];
    text: string[];
  };
  preview_images?: UploadPreview[];
  included_preview_paths: string[];
  excluded_preview_paths: string[];
};

type UploadReference = {
  bucket: string;
  relative_path: string;
  signed_url: string;
};
type UploadUrls = { presentation: UploadReference; articles?: UploadReference };
type ExtractionReference = Pick<UploadReference, "bucket" | "relative_path">;
type UploadReferences = {
  presentation: ExtractionReference;
  articles?: ExtractionReference;
};

type RetryWithoutArticlesResponse = {
  detail?: {
    message?: unknown;
    resolution?: { action?: unknown; label?: unknown };
  };
};

export class RetryWithoutArticlesError extends Error {
  constructor(
    message: string,
    readonly presentation: ExtractionReference,
    readonly label: string
  ) {
    super(message);
  }
}

export async function checkPresentationDuplicate(file: File) {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    await file.arrayBuffer()
  );
  const contentHash = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
  const result = await httpClient.mediaMgmt.post<{ duplicate: boolean }>(
    "/presentations/wizard/check-duplicate",
    { body: { content_hash: contentHash } }
  );
  return result.duplicate;
}

async function uploadFile(
  file: File,
  reference: UploadReference,
  contentType: string
) {
  const response = await fetch(reference.signed_url, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  if (!response.ok) throw new Error("Could not upload the selected file");
}

export async function extractPresentationUpload(
  presentation: File,
  articles: File
) {
  const uploads = await httpClient.mediaMgmt.post<UploadUrls>(
    "/presentations/wizard/upload-urls",
    {
      body: {
        presentation_filename: presentation.name,
        articles_filename: articles.name,
      },
    }
  );
  if (!uploads.articles)
    throw new Error("Article workbook upload was not prepared");

  await Promise.all([
    uploadFile(presentation, uploads.presentation, PRESENTATION_CONTENT_TYPE),
    uploadFile(articles, uploads.articles, WORKBOOK_CONTENT_TYPE),
  ]);

  const presentationReference = {
    bucket: uploads.presentation.bucket,
    relative_path: uploads.presentation.relative_path,
  };
  const articlesReference = {
    bucket: uploads.articles.bucket,
    relative_path: uploads.articles.relative_path,
  };
  let extraction: { media: UploadMedia[]; warnings?: UploadWarning[] };
  try {
    extraction = await extractPresentation(
      presentationReference,
      articlesReference
    );
  } catch (error) {
    const detail = (error instanceof HttpError ? error.body : undefined) as
      RetryWithoutArticlesResponse | undefined;
    if (
      error instanceof HttpError &&
      error.status === 400 &&
      detail?.detail?.resolution?.action === "RETRY_WITHOUT_ARTICLES" &&
      typeof detail.detail.message === "string"
    ) {
      throw new RetryWithoutArticlesError(
        detail.detail.message,
        presentationReference,
        typeof detail.detail.resolution.label === "string"
          ? detail.detail.resolution.label
          : "Continue without article workbook"
      );
    }
    if (
      error instanceof HttpError &&
      typeof error.body === "object" &&
      error.body !== null
    ) {
      const message =
        (error.body as { detail?: unknown; message?: unknown }).detail ??
        (error.body as { message?: unknown }).message;
      if (typeof message === "string" && message) {
        throw new Error(message, { cause: error });
      }
    }
    throw error;
  }

  return {
    candidates: extraction.media,
    presentation: uploads.presentation,
    articles: uploads.articles,
    warnings: extraction.warnings ?? [],
  };
}

async function extractPresentation(
  presentation: ExtractionReference,
  articles?: ExtractionReference
) {
  return httpClient.mediaMgmt.post<{
    media: UploadMedia[];
    warnings?: UploadWarning[];
  }>("/presentations/wizard/extract", {
    body: {
      presentation,
      ...(articles ? { articles } : {}),
    },
  });
}

export async function extractPresentationWithoutArticles(
  presentation: ExtractionReference
) {
  const extraction = await extractPresentation(presentation);
  return { candidates: extraction.media, presentation };
}

export async function confirmPresentationUpload(
  references: Pick<UploadReferences, "presentation" | "articles">,
  candidates: UploadMedia[],
  mediaLaunch: MediaLaunch,
  duplicateAction?: DuplicateAction
) {
  try {
    return await httpClient.mediaMgmt.post<{ media_ids: string[] }>(
      "/presentations/wizard/confirm",
      {
        body: {
          ...(duplicateAction ? { duplicate_action: duplicateAction } : {}),
          presentation: references.presentation,
          launch_name: mediaLaunch.name,
          launch_year: mediaLaunch.year,
          launch_number: mediaLaunch.launch_number ?? null,
          ...(references.articles
            ? {
                articles: {
                  bucket: references.articles.bucket,
                  relative_path: references.articles.relative_path,
                },
              }
            : {}),
          candidates: candidates.map(
            ({ source, preview_images: _previewImages, ...candidate }) => ({
              ...candidate,
              source_slide_number: source.slide_number,
            })
          ),
        },
      }
    );
  } catch (error) {
    if (
      error instanceof HttpError &&
      error.status === 409 &&
      typeof error.body === "object" &&
      error.body !== null
    ) {
      const body = error.body as { detail?: unknown };
      const duplicate = (body.detail ??
        body) as Partial<DuplicatePresentationResponse>;
      if (
        duplicate.code === "DUPLICATE_PRESENTATION" &&
        Array.isArray(duplicate.existing_media)
      ) {
        throw new DuplicatePresentationError(
          duplicate as DuplicatePresentationResponse
        );
      }
    }
    throw error;
  }
}
